/**
 * Generic utility functions for working with arrays.
 */

/**
 * Sorts an array by a specified field using locale-aware string comparison.
 *
 * @param array - The array to sort (can be null/undefined)
 * @param field - The field name (key) to sort by. Can be a dot-separated path for nested properties.
 * @returns A new sorted array, or empty array if input is null/undefined
 *
 * @example
 * ```ts
 * // Sort by a simple property
 * const sorted = sortByLocale(users, 'name')
 *
 * // Sort by a nested property
 * const sorted = sortByLocale(items, 'profile.title')
 *
 * // Handles null/undefined safely
 * const sorted = sortByLocale(null, 'name') // returns []
 * ```
 */
export function sortByLocale<T extends Record<string, unknown>>(
  array: T[] | null | undefined,
  field: string,
): T[] {
  if (!array) {
    return []
  }

  const getValue = (obj: T, path: string): string => {
    const value = path.split('.').reduce<unknown>((acc, key) => {
      if (acc && typeof acc === 'object' && key in acc) {
        return (acc as Record<string, unknown>)[key]
      }
      return undefined
    }, obj)
    if (value == null) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    // For objects, return empty string to avoid '[object Object]'
    return ''
  }

  return [...array].sort((a: T, b: T) => {
    const aValue = getValue(a, field)
    const bValue = getValue(b, field)
    return aValue.localeCompare(bValue)
  })
}
